import { PropsWithChildren, useMemo } from 'react';

import {
  ChakraBox,
  ChakraFlex,
  ChakraText,
  EquipUITheme,
  Modal,
} from '@equip.health/ui';

import logo from '~/assets/img/equipText.png';
import { getAllQuestions, getAnswerTitle } from '~/lib/util/survey.util';
import { removeUrlsFromText } from '~/lib/utils';

const { h6, label, paragraph } = EquipUITheme.typography;

type SurveyResultsProps = {
  isOpen: boolean;
  onClose: () => void;
  patientName: string;
  surveyCompletionDate: string;
  surveyDefinition: SurveyDefinition;
  surveyName: string;
  surveyResults: Record<string, any>;
};

const HeaderItem = ({ children }: PropsWithChildren) => (
  <ChakraText {...paragraph} fontSize="16px" marginBottom="12px">
    {children}
  </ChakraText>
);

const SurveyResults = ({
  isOpen,
  onClose,
  patientName,
  surveyCompletionDate,
  surveyDefinition,
  surveyName,
  surveyResults,
}: SurveyResultsProps) => {
  const allQuestions = useMemo<SurveyQuestion[]>(
    () => (!isOpen ? [] : getAllQuestions(surveyDefinition)),
    [isOpen, surveyDefinition],
  );

  return (
    <Modal
      footerContent={null}
      id="survey-results"
      isOpen={isOpen}
      onClose={onClose}
      size="4xl"
    >
      <ChakraFlex {...label} direction="column" marginBottom="24px">
        <HeaderItem>
          <img alt="Equip logo" src={logo} width="80px" />
        </HeaderItem>
        <HeaderItem>{`${surveyName ?? 'Patient'} Survey`}</HeaderItem>
        <HeaderItem>{patientName}</HeaderItem>
        {surveyCompletionDate && (
          <HeaderItem>{`Completed: ${surveyCompletionDate}`}</HeaderItem>
        )}
      </ChakraFlex>
      {allQuestions.map(({ name, title }) => (
        <ChakraBox
          key={`SurveyResult::${surveyName}::${name}`}
          marginBottom="16px"
        >
          <ChakraText {...h6} marginBottom="8px">
            {title}
          </ChakraText>
          {getAnswerTitle(
            allQuestions,
            name,
            surveyResults?.[name],
            surveyResults?.[`${name}-Comment`] ||
              surveyResults?.[`${name}:-Comment`],
          ).map((answerText: string) => {
            const parsedAnswerText = removeUrlsFromText(answerText, '[Link]');
            return (
              <ChakraText
                {...paragraph}
                key={parsedAnswerText}
                marginLeft="16px"
              >
                {parsedAnswerText}
              </ChakraText>
            );
          })}
        </ChakraBox>
      ))}
    </Modal>
  );
};

export default SurveyResults;
